import { useCallback } from 'react';

import type {
  DownloadDataErrorResponse,
  DownloadDataRequest,
  DownloadDataSuccessResponse,
} from '../data-layer/types';

import { useIspmContext } from '../context/ispmContext';
import { callDownloadApi } from '../data-layer/savi-download';
import { getSaviApiUrlLists, Logger } from '../services';

// type guard for parsed JSON response
function isDownloadJsonResponse(
  data: unknown
): data is DownloadDataSuccessResponse | DownloadDataErrorResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    ('message_code' in data || 'error' in data)
  );
}

function handleFileDownload(response: Blob) {
  const url = window.URL.createObjectURL(response);
  const link = document.createElement('a');
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

  link.href = url;
  link.setAttribute('download', `data_${timestamp}.zip`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { success: true as const };
}

async function handleJsonResponse(response: Blob) {
  const textResponse = await response.text();
  const jsonResponse: unknown = JSON.parse(textResponse);

  if (!isDownloadJsonResponse(jsonResponse)) {
    throw new Error('Invalid JSON response format');
  }

  if ('error' in jsonResponse) {
    Logger.error('Download failed:', jsonResponse.error);

    return { success: false as const, error: jsonResponse.error };
  }

  if (jsonResponse.message_code === 'DOWNLOAD_SUCCESS_MSG') {
    return { success: true as const, message: jsonResponse.message };
  }

  throw new Error('Unexpected JSON response format');
}

// return type interfaces
interface DownloadSuccess {
  success: true;
  message?: string;
}

interface DownloadError {
  success: false;
  error: {
    errorCode: string;
    errorMsg: string;
    type: string;
  };
}

type DownloadResult = DownloadSuccess | DownloadError;

const useDownloadApi = () => {
  /**
   * handles file download requests and returns the result.
   * @param query query string for the download request
   * @returns Promise<DownloadResult> result of the download operation
   */

  const { ispmState } = useIspmContext();
  const { DATA_DOWNLOAD_URL } = getSaviApiUrlLists(ispmState.useMockServerApis);

  const handleDownload = useCallback(
    async (query: string): Promise<DownloadResult> => {
      try {
        const request: DownloadDataRequest = {
          query,
        };

        const response = await callDownloadApi(request, DATA_DOWNLOAD_URL);

        Logger.info('Download response type:', {
          isBlob: response instanceof Blob,
          type: response.constructor.name,
          response,
        });

        // set both types of responses as blob for consistenct handling.
        // we're using axios's blob handling as a transport mechanism,
        // and the backend contracts are honored. the api service handles
        // data transport, and this hook handles response interpretation.
        if (response instanceof Blob) {
          if (response.type === 'application/json') {
            return await handleJsonResponse(response);
          }

          if (response.type === 'application/octet-stream') {
            return handleFileDownload(response);
          }
        }

        // unexpected response format
        Logger.error('Unexpected response format:', response);

        return {
          success: false,
          error: {
            errorCode: 'UI-E03',
            errorMsg: 'Unexpected response format',
            type: 'UI',
          },
        };
      } catch (error) {
        Logger.error('Error in download:', error);

        return {
          success: false,
          error: {
            errorCode: 'UI-E02',
            errorMsg: 'Failed to download data',
            type: 'UI',
          },
        };
      }
    },
    [DATA_DOWNLOAD_URL]
  );

  return { handleDownload };
};

export default useDownloadApi;
